import { iconForName } from "@/components/artifacts/iconForName.ts";
import { AppletPreviewBlock } from "@/components/artifacts/view/blocks/AppletPreviewBlock.tsx";
import { AudioPlayerBlock } from "@/components/artifacts/view/blocks/AudioPlayerBlock.tsx";
import { BadgeBlock } from "@/components/artifacts/view/blocks/BadgeBlock.tsx";
import { DocumentExportBlock } from "@/components/artifacts/view/blocks/DocumentExportBlock.tsx";
import { CardBlock } from "@/components/artifacts/view/blocks/CardBlock.tsx";
import { ConflictCheckBlock } from "@/components/artifacts/view/blocks/ConflictCheckBlock.tsx";
import { CronScheduleBlock } from "@/components/artifacts/view/blocks/CronScheduleBlock.tsx";
import { DateTimeRangeBlock } from "@/components/artifacts/view/blocks/DateTimeRangeBlock.tsx";
import { DiffBlock } from "@/components/artifacts/view/blocks/DiffBlock.tsx";
import { DividerBlock } from "@/components/artifacts/view/blocks/DividerBlock.tsx";
import { EmailAttachmentsBlock } from "@/components/artifacts/view/blocks/EmailAttachmentsBlock.tsx";
import { EmailLinkBlock } from "@/components/artifacts/view/blocks/EmailLinkBlock.tsx";
import { EmailRecipientsBlock } from "@/components/artifacts/view/blocks/EmailRecipientsBlock.tsx";
import { FieldRowBlock } from "@/components/artifacts/view/blocks/FieldRowBlock.tsx";
import { HorizontalGroupBlock } from "@/components/artifacts/view/blocks/HorizontalGroupBlock.tsx";
import { ImagePreviewBlock } from "@/components/artifacts/view/blocks/ImagePreviewBlock.tsx";
import { InfoBannerBlock } from "@/components/artifacts/view/blocks/InfoBannerBlock.tsx";
import { InstantBookPickerBlock } from "@/components/artifacts/view/blocks/InstantBookPickerBlock.tsx";
import { KickoffBodyWithSlotsBlock } from "@/components/artifacts/view/blocks/KickoffBodyWithSlotsBlock.tsx";
import { LinkButtonBlock } from "@/components/artifacts/view/blocks/LinkButtonBlock.tsx";
import { LocationBlock } from "@/components/artifacts/view/blocks/LocationBlock.tsx";
import { NumberInputBlock } from "@/components/artifacts/view/blocks/NumberInputBlock.tsx";
import { ConnectorHeaderBlock } from "@/components/artifacts/view/blocks/ConnectorHeaderBlock.tsx";
import { PickerBlock } from "@/components/artifacts/view/blocks/PickerBlock.tsx";
import { ComputerDesktopBlock } from "@/components/artifacts/view/blocks/ComputerDesktopBlock.tsx";
import { SecretInputBlock } from "@/components/artifacts/view/blocks/SecretInputBlock.tsx";
import { PersonHeaderBlock } from "@/components/artifacts/view/blocks/PersonHeaderBlock.tsx";
import { ParticipantListBlock } from "@/components/artifacts/view/blocks/ParticipantListBlock.tsx";
import { RecurrenceBlock } from "@/components/artifacts/view/blocks/RecurrenceBlock.tsx";
import { RemindersBlock } from "@/components/artifacts/view/blocks/RemindersBlock.tsx";
import { RichTextBodyBlock } from "@/components/artifacts/view/blocks/RichTextBodyBlock.tsx";
import { SandboxedHtmlBlock } from "@/components/artifacts/view/blocks/SandboxedHtmlBlock.tsx";
import { SchedulingMadlibsBlock } from "@/components/artifacts/view/blocks/SchedulingMadlibsBlock.tsx";
import { SelectBlock } from "@/components/artifacts/view/blocks/SelectBlock.tsx";
import { StatusBadgeBlock } from "@/components/artifacts/view/blocks/StatusBadgeBlock.tsx";
import { SubjectLineBlock } from "@/components/artifacts/view/blocks/SubjectLineBlock.tsx";
import { TogglePillBlock } from "@/components/artifacts/view/blocks/TogglePillBlock.tsx";
import { TranscriptBlock } from "@/components/artifacts/view/blocks/TranscriptBlock.tsx";
import { VirtualMeetingBlock } from "@/components/artifacts/view/blocks/VirtualMeetingBlock.tsx";
import { warnUnsupportedValue } from "@/lib/error.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import type { HeaderIcon } from "@/types/artifacts.ts";

const SectionLabel = ({
  text,
  icon,
  variant,
}: {
  text: string;
  icon: HeaderIcon | undefined;
  variant: "normal" | "caption" | undefined;
}) => {
  const Icon = icon ? iconForName(icon) : null;
  if (variant === "caption") {
    return (
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="size-3.5 text-foreground" /> : null}
        {/* Foreground rather than muted: a caption heads a section, and a
            heading the eye can find has to be darker than the rows under it. */}
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground">
          {text}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-sm @md:text-base text-foreground font-semibold">
      {Icon ? <Icon className="size-4" /> : null}
      <span>{text}</span>
    </div>
  );
};

/**
 * Per-component dispatch. Lives in its own file so layout containers
 * (`HorizontalGroupBlock`, future column/grid components) can recurse into
 * it without creating an import cycle with `ArtifactView`.
 *
 * (The React renderer files still carry the legacy "Block" suffix —
 * `HeaderBlock.tsx` etc. — to keep this rename focused on the schema/wire
 * vocabulary. The React component layer can be renamed later if it bothers
 * anyone.)
 */
export const renderComponent = (component: ArtifactComponent) => {
  switch (component.type) {
    case "text":
      return <SubjectLineBlock component={component} />;
    case "markdown":
      return <RichTextBodyBlock component={component} />;
    case "participantList":
      return <ParticipantListBlock component={component} />;
    case "emailRecipients":
      return <EmailRecipientsBlock component={component} />;
    case "emailAttachments":
      return <EmailAttachmentsBlock component={component} />;
    case "dateTimeRange":
      return <DateTimeRangeBlock component={component} />;
    case "recurrence":
      return <RecurrenceBlock component={component} />;
    case "reminders":
      return <RemindersBlock component={component} />;
    case "location":
      return <LocationBlock component={component} />;
    case "virtualMeeting":
      return <VirtualMeetingBlock component={component} />;
    case "infoBanner":
      return <InfoBannerBlock component={component} />;
    case "toggle":
      return <TogglePillBlock component={component} />;
    case "schedulingMadlibs":
      return <SchedulingMadlibsBlock component={component} />;
    case "kickoffBodyWithSlots":
      return <KickoffBodyWithSlotsBlock component={component} />;
    case "instantBookPicker":
      return <InstantBookPickerBlock component={component} />;
    case "select":
      return <SelectBlock component={component} />;
    case "numberInput":
      return <NumberInputBlock component={component} />;
    case "cronSchedule":
      return <CronScheduleBlock component={component} />;
    case "sectionLabel":
      return (
        <SectionLabel text={component.text} icon={component.icon} variant={component.variant} />
      );
    case "divider":
      return <DividerBlock component={component} />;
    case "horizontalGroup":
      // Renderer is injected as a prop to avoid the
      // `HorizontalGroupBlock <-> renderComponent` import cycle. Children
      // are typed as `LeafComponent`, which is a subset of
      // `ArtifactComponent`, so passing `renderComponent` (broader
      // parameter) satisfies the prop type.
      return <HorizontalGroupBlock component={component} renderLeaf={renderComponent} />;
    case "card":
      // Same injected-renderer pattern as horizontalGroup to avoid the
      // CardBlock <-> renderComponent import cycle.
      return <CardBlock component={component} renderLeaf={renderComponent} />;
    case "personHeader":
      return <PersonHeaderBlock component={component} />;
    case "connectorHeader":
      return <ConnectorHeaderBlock component={component} />;
    case "picker":
      return <PickerBlock component={component} />;
    case "secretInput":
      return (
        <SecretInputBlock
          question={component.question}
          hint={component.hint}
          host={component.host}
          savedText={component.savedText}
          submitLabel={component.submitLabel}
          submitPath={component.submitPath}
          state={component.state}
        />
      );
    case "computerDesktop":
      return (
        <ComputerDesktopBlock
          openPath={component.openPath}
          closePath={component.closePath}
          pastePath={component.pastePath}
        />
      );
    case "statusBadge":
      return <StatusBadgeBlock component={component} />;
    case "fieldRow":
      return <FieldRowBlock component={component} />;
    case "diff":
      return <DiffBlock component={component} />;
    case "imagePreview":
      return <ImagePreviewBlock component={component} />;
    case "audioPlayer":
      return <AudioPlayerBlock component={component} />;
    case "linkButton":
      return <LinkButtonBlock component={component} />;
    case "emailLink":
      return <EmailLinkBlock component={component} />;
    case "conflictCheck":
      return <ConflictCheckBlock component={component} />;
    case "transcript":
      return <TranscriptBlock component={component} />;
    case "badge":
      return <BadgeBlock component={component} />;
    case "sandboxedHtml":
      return <SandboxedHtmlBlock component={component} />;
    case "appletPreview":
      return <AppletPreviewBlock component={component} />;
    case "documentExport":
      return <DocumentExportBlock component={component} />;
    default:
      // Skip unknown component types instead of throwing: the view is
      // server-driven, so a client whose bundle predates a newly added block
      // type (a stale tab, or the admin debug viewer rendering a blob produced
      // by newer code) must degrade to omitting that block — not crash the
      // whole artifact card. The `never` param still enforces compile-time
      // exhaustiveness for types this bundle knows about.
      warnUnsupportedValue(component, `Unknown artifact component type — skipping render`);
      return null;
  }
};
